/**
 * Materiality report page HTML templates (from Materiality Assessment Report Template.docx).
 * Red-styled spans = dynamic placeholders filled from assessment / company data.
 */

const escapeHtml = (text) =>
	String(text)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');

const DYN = (label) =>
	`<span style="color: #dc3545;">&lt;${escapeHtml(label)}&gt;</span>`;

const PAGE_STYLE =
	'font-family: Arial, Helvetica, sans-serif; padding: 20px 24px; color: #334155; line-height: 1.6;';

const PAGE_SECTION_NUMBERS = {
	'Executive Summary': 1,
	'About Company': 2,
	'Assessment Methodology': 3,
	'Stakeholder Identification & Engagement': 4,
	'Peer Benchmarking': 5,
	'Material Topics Overview': 6,
	'Material Topics — Scorecard': 7,
	'Materiality Matrix': 8,
	'Material Topics — GRI & SDG Mapping': 9,
	'Material Topics — Key Data Points': 10,
	'Actions & Recommendations': 11,
	Conclusion: 12,
};

const pageHeading = (pageName) => {
	const num = PAGE_SECTION_NUMBERS[pageName];
	if (!num) {
		return '';
	}
	const titleContent =
		pageName === 'About Company'
			? `About ${DYN('Company Name')}`
			: escapeHtml(pageName);
	return `<h1 class="mat-report-section-title" style="font-size: 22pt; font-weight: 700; color: #2dc26b; margin: 0 0 28px; line-height: 1.2;">${num}. ${titleContent}</h1>`;
};

const wrapPage = (pageName, body) =>
	`<div class="materiality-report-page mat-report-template-v2" style="${PAGE_STYLE}">${pageHeading(pageName)}${body}</div>`;

export const prependMaterialitySectionTitle = (pageName, html) => {
	if (!html || !PAGE_SECTION_NUMBERS[pageName] || html.includes('mat-report-section-title')) {
		return html;
	}
	return `${pageHeading(pageName)}${html}`;
};

const h2 = (text) =>
	`<h2 style="font-size: 14pt; font-weight: 700; color: #1e293b; margin: 24px 0 12px;">${text}</h2>`;

const h3 = (text) =>
	`<h3 style="font-size: 12pt; font-weight: 700; color: #334155; margin: 18px 0 8px;">${text}</h3>`;

const p = (html) => `<p style="margin: 0 0 12px;">${html}</p>`;

const ul = (items) =>
	`<ul style="margin: 0 0 12px; padding-left: 24px;">${items
		.map((item) => `<li style="margin-bottom: 6px;">${item}</li>`)
		.join('')}</ul>`;

const simpleTable = (headers, rows) => {
	const th = headers
		.map(
			(h) =>
				`<th style="border: 1px solid #e5e7eb; padding: 8px 10px; background: #f8fafc; text-align: left; font-size: 10pt;">${h}</th>`
		)
		.join('');
	const tr = rows
		.map(
			(cells) =>
				`<tr>${cells
					.map(
						(c) =>
							`<td style="border: 1px solid #e5e7eb; padding: 8px 10px; font-size: 10pt; vertical-align: top;">${c}</td>`
					)
					.join('')}</tr>`
		)
		.join('');
	return `<table style="width: 100%; border-collapse: collapse; margin: 12px 0 20px; font-size: 10pt;"><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table>`;
};

const PAGE_TEMPLATES = {
	'Executive Summary': () =>
		wrapPage('Executive Summary', `
			${p(
				`This report presents the findings of the ${DYN('Single/Double Materiality Assessment (Impact Materiality)')} conducted for ${DYN('Company Full Name')} for the financial year ${DYN('20xx–xx')}. The assessment was undertaken to identify, evaluate, and prioritise environmental, social, and governance (ESG) topics that are material to the company's operations and its impact on stakeholders, society, and the environment.`
			)}
			${p(DYN('About the company from company details'))}
			${p(
				`${DYN('Company Name')} undertakes ESG reporting to meet evolving expectations from investors, customers, and other key stakeholders who increasingly prioritise transparency and sustainability performance in their decision-making. It enables the company to systematically document its sustainability initiatives, track progress against goals, and enhance the quality and consistency of disclosures.`
			)}
			${h2('Key Findings')}
			${ul([
				`${DYN('No.')} material topics were identified and prioritised across Environmental, Social, and Governance dimensions.`,
				`${DYN('Name of Material issues eg: Regulatory Compliance, Data Security &amp; Privacy, and Corporate Governance &amp; Business Ethics')} emerged as the top-ranked material issues.`,
				`A total of ${DYN('No.')} stakeholders were surveyed, with ${DYN('No.')} responses received — a response rate of ${DYN('No.%')}.`,
				`Four stakeholder categories were engaged with respective weightages for each category: Employees (${DYN('No.%')}), Suppliers (${DYN('No.%')}), Customers (${DYN('No.%')}), and Investors &amp; Partners (${DYN('No.%')}).`,
				`Peer benchmarking was conducted against ${DYN('Peer Names')}, as well as global rating agencies ${DYN('Agency name eg: S&amp;P Global, MSCI')} listed topics per sector.`,
			])}
		`),

	'About Company': () =>
		wrapPage('About Company', `
			${h2('Company Overview')}
			${p(DYN('Company overview from company details / profile'))}
			${h2('Service Portfolio')}
			${ul([
				DYN('Service 1 with brief description'),
				DYN('Service 2 with brief description'),
				DYN('Service 3 with brief description'),
			])}
			${h2('ESG Commitment')}
			${p(
				`${DYN('Company Name')} undertakes ESG reporting to meet evolving expectations from investors, customers, and other key stakeholders, who increasingly prioritize transparency and sustainability performance in their decision-making. It enables the company to systematically document its sustainability initiatives, track progress against goals, and enhance the quality and consistency of disclosures. ESG reporting also strengthens ${DYN('Company Name')}'s positioning with hyperscale and enterprise clients, who seek responsible and future-ready infrastructure partners, while supporting more informed internal decision-making and continuous improvement across environmental, social, and governance priorities.`
			)}
		`),

	'Assessment Methodology': () =>
		wrapPage('Assessment Methodology', `
			${p(
				`The ${DYN('Single/Double')} Materiality Assessment was completed in the following key phases, aligned with recognised frameworks including the ${DYN('Global Reporting Initiative (GRI) Standards')}.`
			)}
			${h3('3.1 Understanding')}
			${p(
				`The first step involved understanding key material topics and associated impacts, risks, and opportunities integral to ${DYN('Company Name')}'s business model and data landscape. This included:`
			)}
			${ul([
				'In-depth research on the data center industry and sector-specific ESG considerations',
				'Review of sector-agnostic global sustainability standards',
				"Consideration of geographical, economic, political, and legal risks applicable to the organisation's operating context",
				`Analysis of ${DYN('Company Name')}'s existing sustainability initiatives and ESG disclosure practices`,
			])}
			${h3('3.2 Identification')}
			${p('This phase involved identification of actual and potential impacts, risks, and opportunities through:')}
			${ul([
				`<strong>Peer Benchmarking:</strong> Reviewed material topics reported by leading operators — ${DYN('Peer Names')} — to understand industry-wide ESG priorities`,
				`<strong>ESG Rating Agency Review:</strong> Reviewed sector-specific ESG materiality maps from ${DYN('S&amp;P Global, MSCI')} to understand expectations`,
				`<strong>Standards Review:</strong> Reviewed ${DYN('Company Name')}'s list of sustainability matters against ${DYN('global standards (GRI)')}`,
				'<strong>Stakeholder Identification:</strong> Identified four key stakeholder categories and assigned weightages based on their influence and interest',
			])}
			${h3('3.3 Assessment')}
			${p('The assessment of material topics was conducted across two dimensions:')}
			${p('<strong>Impact Materiality Assessment</strong><br/>Evaluated the impact of identified sustainability issues on the environment and society and how important it is for the company to focus on and act on this topic.')}
			${p('<strong>Stakeholder Engagement</strong><br/>Stakeholder engagement was conducted through a structured digital survey. Stakeholders were asked to evaluate the identified ESG issues, taking into account the impact of materiality perspectives. The survey was distributed to ' + DYN('No.') + ' individuals across four stakeholder categories, with ' + DYN('No.') + ' responses received (response rate: ' + DYN('No. %') + ').')}
			${h3('3.4 Determination')}
			${p("Material topics were ranked by combining stakeholder survey scores with the company's internal assessment of impact materiality. Topics that surpassed the materiality threshold were prioritised for disclosure and strategic management.")}
		`),

	'Stakeholder Identification & Engagement': () =>
		wrapPage('Stakeholder Identification & Engagement', `
			${h2('Stakeholder Categories')}
			${p('The following four stakeholder categories were identified and engaged as part of the materiality assessment process:')}
			${simpleTable(
				['Stakeholder Group', 'Weightage', 'Survey Status', 'Key Topics of Interest'],
				[
					['Employees', DYN('No. %'), 'Engaged', DYN('Name of Topics sent to employees')],
					['Suppliers', DYN('No. %'), 'Engaged', DYN('Name of Topics sent to suppliers')],
					['Customers', DYN('No. %'), 'Engaged', DYN('Name of Topics sent to customers')],
					['Investors &amp; Partners', DYN('No. %'), 'Engaged', DYN('Name of Topics sent to investors &amp; Partners')],
				]
			)}
			${h2('Survey Statistics')}
			${simpleTable(
				['Total Invitations Sent', 'Total Responses Received', 'Response Rate', 'Survey Period'],
				[[DYN('No.'), DYN('No.'), DYN('No. %'), `FY ${DYN('20xx–xx')}`]]
			)}
			${p(`The high response rate of ${DYN('No.%')} reflects strong stakeholder engagement and confidence in the process.`)}
		`),

	'Peer Benchmarking': () =>
		wrapPage('Peer Benchmarking', `
			${p(
				`As part of the identification phase, ${DYN('Company')}'s material topics were benchmarked against leading global and regional data center operators to validate relevance and comprehensiveness. The benchmarking covered the following peers:`
			)}
			${ul([
				`${DYN('Peer 1')} — ${DYN('One liner about Peer 1')}`,
				`${DYN('Peer 2')} — ${DYN('One liner about Peer 2')}`,
				`${DYN('Peer 3')} — ${DYN('One liner about Peer 3')}`,
			])}
			${p(
				`The benchmark confirms that all ${DYN('No.')} material topics identified for ${DYN('Company')} are consistent with or broader than industry peers, validating the comprehensiveness of the assessment.`
			)}
			${simpleTable(
				['Material Topic', DYN('Peer 1'), DYN('Peer 2'), DYN('Peer 3')],
				Array.from({ length: 5 }, () => [DYN('Topic name'), DYN('✓/-'), DYN('✓/-'), DYN('✓/-')])
			)}
			${p('<em>Legend: ✓ = Topic reported/material for peer; - = Not explicitly reported</em>')}
			${p(DYN('Peer benchmarking comparison table — populated dynamically'))}
		`),

	'Material Topics Overview': () =>
		wrapPage('Material Topics Overview', `
			${p(
				`Following the assessment process, ${DYN('No.')} topics were identified as material for ${DYN('Company Full Name')} across the three ESG dimensions. Each topic is defined below with its relevance to ${DYN('Company')}'s operations:`
			)}
			${h3('ENVIRONMENTAL')}
			${p(`<strong>${DYN('Topic name')}</strong><br/>${DYN('Topic description')}`)}
			${p(`<strong>${DYN('Topic name')}</strong><br/>${DYN('Topic description')}`)}
			${h3('SOCIAL')}
			${p(`<strong>${DYN('Topic name')}</strong><br/>${DYN('Topic description')}`)}
			${p(`<strong>${DYN('Topic name')}</strong><br/>${DYN('Topic description')}`)}
			${h3('GOVERNANCE')}
			${p(`<strong>${DYN('Topic name')}</strong><br/>${DYN('Topic description')}`)}
			${p(`<strong>${DYN('Topic name')}</strong><br/>${DYN('Topic description')}`)}
			${p(DYN('Material topics by dimension — populated dynamically'))}
		`),

	'Material Topics — Scorecard': () =>
		wrapPage('Material Topics — Scorecard', `
			${p(
				`The table below presents the final ranked list of material topics for ${DYN('Company Full Name')}, combining the weighted stakeholder survey scores with the company's internal impact materiality assessment. Topics are listed in order of overall materiality ranking.`
			)}
			${simpleTable(
				['Rank', 'Material Topic', 'Dimension', 'Impact Materiality Score', 'Stakeholder Importance Score'],
				[
					['1', DYN('Topic Name'), 'Governance', DYN('No.'), DYN('No.')],
					['2', DYN('Topic Name'), 'Social', DYN('No.'), DYN('No.')],
					['3', DYN('Topic Name'), 'Environment', DYN('No.'), DYN('No.')],
				]
			)}
			${p(
				`The scorecard confirms that Governance topics — particularly ${DYN('3 Top Topic Names: eg: Regulatory Compliance, Data Security &amp; Privacy, and Corporate Governance')} — consistently rank highest, reflecting ${DYN('Company')}'s critical role as custodian of sensitive data and a regulated infrastructure provider, followed by health &amp; safety, customer value and energy management as the top priorities.`
			)}
			${p(DYN('Scorecard table — populated dynamically from finalized scorecard'))}
		`),

	'Materiality Matrix': () =>
		wrapPage('Materiality Matrix', `
			${p(
				`The materiality matrix below maps all ${DYN('No. of topics')} assessed topics across two axes: Impact Materiality (the significance of the organisation's impact on the environment and society) and Stakeholder Importance (the degree to which stakeholders consider the topic critical for ${DYN('Company')} to manage). Topics in the upper-right quadrant represent the highest-priority areas for focused management and disclosure.`
			)}
			${p(
				`<strong>MATERIALITY MATRIX</strong> — ${DYN('Company Name')} FY ${DYN('20xx–xx')}<br/>X-Axis: Stakeholder Importance Score | Y-Axis: Impact Materiality Score`
			)}
			${ul([
				`<strong>HIGH IMPACT / HIGH STAKEHOLDER IMPORTANCE (Priority zone):</strong> ${DYN('[Rank] Topic Name (Impact materiality score / Stakeholder importance score)')}`,
				`<strong>HIGH IMPACT / LOWER STAKEHOLDER IMPORTANCE:</strong> Monitor &amp; Communicate — ${DYN('[Rank] Topic Name (scores)')}`,
				`<strong>LOWER IMPACT / HIGH STAKEHOLDER IMPORTANCE:</strong> Engage &amp; Disclose — ${DYN('[Rank] Topic Name (scores)')}`,
				`<strong>LOWER IMPACT / LOWER STAKEHOLDER IMPORTANCE:</strong> Review Periodically — ${DYN('[Rank] Topic Name (scores)')}`,
			])}
			${p('■ Environmental &nbsp; ■ Social &nbsp; ■ Governance')}
			${p(DYN('Materiality matrix chart — populated dynamically'))}
		`),

	'Material Topics — GRI & SDG Mapping': () =>
		wrapPage('Material Topics — GRI & SDG Mapping', `
			${p(
				`The table below maps each of ${DYN('Company')}'s ${DYN('No.')} material topics to the relevant GRI Standards disclosures and United Nations Sustainable Development Goals (SDGs). This mapping supports structured ESG reporting and demonstrates alignment with globally recognised frameworks and national development priorities.`
			)}
			${simpleTable(
				['Dimension', 'Material Topic', 'GRI Standards', 'UN SDG Alignment'],
				[
					['Environment', DYN('Topic Name'), DYN('GRI XXX: Standard Name 20XX'), DYN('SDG XX: Goal Name')],
					['Social', DYN('Topic Name'), DYN('GRI XXX: Standard Name 20XX'), DYN('SDG XX: Goal Name')],
					['Governance', DYN('Topic Name'), DYN('GRI XXX: Standard Name 20XX'), DYN('SDG XX: Goal Name')],
				]
			)}
			${p(DYN('GRI &amp; SDG mapping table — populated dynamically'))}
		`),

	'Material Topics — Key Data Points': () =>
		wrapPage('Material Topics — Key Data Points', `
			${p(
				`The following data points should be tracked, measured, and disclosed for each material topic. These align with GRI Standards disclosure requirements and form the basis for ${DYN('Company')}'s ESG data collection programme.`
			)}
			${h3(`Environment · ${DYN('Topic Name')}`)}
			${ul([DYN('Data Point 1'), DYN('Data Point 2'), DYN('Data Point 3')])}
			${h3(`Social · ${DYN('Topic Name')}`)}
			${ul([DYN('Data Point 1'), DYN('Data Point 2'), DYN('Data Point 3')])}
			${h3(`Governance · ${DYN('Topic Name')}`)}
			${ul([DYN('Data Point 1'), DYN('Data Point 2'), DYN('Data Point 3')])}
			${p(DYN('Key data points by topic — populated dynamically'))}
		`),

	'Actions & Recommendations': () =>
		wrapPage('Actions & Recommendations', `
			${p(`Based on the outcome of the materiality assessment, the following strategic actions and recommendations are proposed for ${DYN('Company Full Name')}:`)}
			${h3('Short-Term Actions (0–6 months)')}
			${ul([DYN('Action 1: Explanation in one line'), DYN('Action 2: Explanation in one line'), DYN('Action 3: Explanation in one line')])}
			${h3('Medium-Term Actions (6 months – 2 years)')}
			${ul([DYN('Action 1: Explanation in one line'), DYN('Action 2: Explanation in one line'), DYN('Action 3: Explanation in one line')])}
			${h3('Long-Term Actions (2+ years)')}
			${ul([DYN('Action 1: Explanation in one line'), DYN('Action 2: Explanation in one line'), DYN('Action 3: Explanation in one line')])}
		`),

	Conclusion: () =>
		wrapPage('Conclusion', `
			${p(
				`The ${DYN('Single/Double')} Materiality Assessment for ${DYN('Company Full Name')} has successfully identified ${DYN('No.')} material ESG topics that are most significant to the company's operations and its impact on stakeholders, society, and the environment. The process was conducted with rigour and stakeholder inclusivity, achieving a ${DYN('No.%')} survey response rate across ${DYN('No.')} invitees from ${DYN('No. in words')} key stakeholder categories.`
			)}
			${p(
				`The assessment validates that ${DYN('Company')}'s most critical ESG priorities align closely with those of its global peers in the data center sector — particularly in the areas of ${DYN('Four top priority topics')}. These findings will form the backbone of ${DYN('Company')}'s ESG reporting strategy and guide the company's sustainability investments and disclosures for the coming years.`
			)}
			${p(
				`By leveraging this materiality assessment as a living document, reviewed and refreshed periodically, ${DYN('Company')} is well-positioned to build credibility with its investors, customers, and the broader stakeholder community — reinforcing its commitment to being a responsible, future-ready digital infrastructure partner.`
			)}
			${p(
				`This report has been prepared by the ESG Advisory Team on behalf of ${DYN('Company Full Name')}. The findings are based on stakeholder engagement data, peer benchmarking, and internal assessments conducted during FY ${DYN('20xx–xx')}.`
			)}
		`),
};

export const getMaterialityPageTemplateHtml = (pageName) => {
	const builder = PAGE_TEMPLATES[pageName];
	return builder ? builder() : null;
};

export const hasMaterialityPageTemplate = (pageName) => Boolean(PAGE_TEMPLATES[pageName]);
